import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  MoreHorizontal,
  CreditCard,
  Loader2,
  Eye,
  XCircle,
  CheckCircle,
  ArrowUpRight,
  ArrowDownLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { useSessionState } from '@/hooks/use-session-state';

const paymentTypeConfig: Record<string, { color: string; icon: React.ElementType }> = {
  CUSTOMER_PAYMENT: { color: 'bg-green-100 text-green-700', icon: ArrowDownLeft },
  SUPPLIER_PAYMENT: { color: 'bg-blue-100 text-blue-700', icon: ArrowUpRight },
};

const paymentMethodConfig: Record<string, string> = {
  CASH: 'Cash',
  CHECK: 'Check',
  BANK_TRANSFER: 'Bank Transfer',
  CREDIT_CARD: 'Credit Card',
  DEBIT_CARD: 'Debit Card',
  PAYPAL: 'PayPal',
  OTHER: 'Other',
};

export function TradingPaymentsPage() {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const [debouncedSearch, setDebouncedSearch] = useSessionState(
    'trading:payments:debouncedSearch',
    ''
  );
  const [searchQuery, setSearchQuery] = useState(debouncedSearch);
  const [selectedType, setSelectedType] = useSessionState<string>(
    'trading:payments:selectedType',
    'all'
  );
  const [page, setPage] = useState(1);
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
  const [paymentToVoid, setPaymentToVoid] = useState<string | null>(null);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  };

  const { data, isLoading, error } = trpc.tradingPayment.list.useQuery({
    search: debouncedSearch || undefined,
    type:
      selectedType !== 'all'
        ? (selectedType as 'CUSTOMER_PAYMENT' | 'SUPPLIER_PAYMENT')
        : undefined,
    page,
  });

  const utils = trpc.useUtils();

  const voidMutation = trpc.tradingPayment.void.useMutation({
    onSuccess: () => {
      utils.tradingPayment.list.invalidate();
      showSuccessToast(t('finance.payments.voided'));
      setVoidConfirmOpen(false);
      setPaymentToVoid(null);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const payments = data?.payments ?? [];
  const pagination = data?.pagination;

  const handleVoidPayment = (id: string) => {
    setPaymentToVoid(id);
    setVoidConfirmOpen(true);
  };

  const confirmVoid = () => {
    if (paymentToVoid) {
      voidMutation.mutate({ id: paymentToVoid, reason: 'Voided by user' });
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'CUSTOMER_PAYMENT':
        return t('finance.payments.typeCustomer');
      case 'SUPPLIER_PAYMENT':
        return t('finance.payments.typeSupplier');
      default:
        return type;
    }
  };

  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(value);
  };

  const formatDate = (dateValue: string | Date) => {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const types = ['CUSTOMER_PAYMENT', 'SUPPLIER_PAYMENT'];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('finance.payments.title')}</h1>
          <p className="text-muted-foreground">{t('finance.payments.subtitle')}</p>
        </div>
        <Button onClick={() => navigate('/trading/finance/payments/new')}>
          <Plus className="mr-2 h-4 w-4" />
          {t('finance.payments.recordPayment')}
        </Button>
      </div>

      {/* Void Confirmation Dialog */}
      <ConfirmationDialog
        open={voidConfirmOpen}
        onOpenChange={setVoidConfirmOpen}
        title={t('finance.payments.void')}
        description={t('finance.payments.voidConfirm')}
        confirmLabel={t('finance.payments.void')}
        variant="destructive"
        onConfirm={confirmVoid}
        isLoading={voidMutation.isPending}
      />

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            placeholder={t('finance.payments.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 w-full border pl-10 pr-4 text-sm focus:outline-none focus:ring-1"
          />
        </div>
        <Select value={selectedType} onValueChange={setSelectedType}>
          <SelectTrigger className="border-border focus:border-foreground focus:ring-foreground/20 h-8 w-[180px] border focus:ring-1">
            <SelectValue placeholder={t('finance.payments.selectType')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('finance.payments.allTypes')}</SelectItem>
            {types.map((type) => (
              <SelectItem key={type} value={type}>
                {getTypeLabel(type)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <span className="text-muted-foreground ml-2">{t('common.loading')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('common.error')}</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && payments.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <CreditCard className="text-muted-foreground/50 h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">{t('finance.payments.noPayments')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {t('finance.payments.noPaymentsDesc')}
          </p>
          <Button className="mt-4" onClick={() => navigate('/trading/finance/payments/new')}>
            <Plus className="mr-2 h-4 w-4" />
            {t('finance.payments.recordPayment')}
          </Button>
        </div>
      )}

      {/* Payments Table */}
      {!isLoading && !error && payments.length > 0 && (
        <>
          <div className="border-border bg-card overflow-hidden border">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('finance.payments.paymentNumber')}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium">
                    {t('finance.payments.type')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('finance.payments.partyName')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('finance.payments.reference')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('finance.payments.method')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('finance.payments.date')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('finance.payments.amount')}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium">
                    {t('finance.payments.status')}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium">
                    {tCommon('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {payments.map((payment) => {
                  const typeConfig =
                    paymentTypeConfig[payment.type] || paymentTypeConfig.CUSTOMER_PAYMENT;
                  const TypeIcon = typeConfig?.icon ?? CreditCard;
                  const partyName =
                    payment.type === 'CUSTOMER_PAYMENT'
                      ? payment.customerName
                      : payment.supplierName;

                  return (
                    <tr key={payment.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <Link
                          to={`/trading/finance/payments/${payment.id}`}
                          className="text-primary font-mono text-sm font-medium hover:underline"
                        >
                          {payment.paymentNumber}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${typeConfig?.color ?? 'bg-gray-100 text-gray-700'}`}
                        >
                          <TypeIcon className="h-3 w-3" />
                          {getTypeLabel(payment.type)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium">{partyName || '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {payment.invoiceNumber || payment.supplierInvoiceNumber || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {paymentMethodConfig[payment.paymentMethod] || payment.paymentMethod}
                      </td>
                      <td className="px-4 py-3 text-sm">{formatDate(payment.paymentDate)}</td>
                      <td className="px-4 py-3 text-right text-sm font-medium">
                        {formatCurrency(Number(payment.amount), payment.currency)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1 bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                          <CheckCircle className="h-3 w-3" />
                          {t('finance.payments.active')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => navigate(`/trading/finance/payments/${payment.id}`)}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              {tCommon('view')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => handleVoidPayment(payment.id)}
                            >
                              <XCircle className="mr-2 h-4 w-4" />
                              {t('finance.payments.void')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-sm">
                {tCommon('showing')} {(pagination.page - 1) * pagination.limit + 1} -{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} {tCommon('of')}{' '}
                {pagination.total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="border-border hover:bg-muted focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1 disabled:opacity-50"
                >
                  {tCommon('previous')}
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === pagination.totalPages}
                  className="border-border hover:bg-muted focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1 disabled:opacity-50"
                >
                  {tCommon('next')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
